// client/src/components/level-fifteen-view.tsx
//
// LVL 15 "Road to Domination" endgame view.
// Rendered from the GBP Optimization page when `level >= 15`.
//
// Data contract (see README at bottom of this file):
//   - businessName: string
//   - state:        RawLevelFifteenState (booleans + activity counters)
//   - powerUps:     LevelFifteenPowerUps (derived from state via derivePowerUps)
//   - hasWebsite:   boolean (true if the account has a live website)
//   - onPurchase(productId): called when a paid power-up is activated OR a goal
//                            counter is incremented.
//   - onToggleUpgrade(key):  called for fallback toggles like gbpPictureUpgrade.
//
// Deps: react, framer-motion, lucide-react, tailwindcss.
// Optional: replace the local `cn` with your own utility if you have one
// (e.g. the shadcn `@/lib/utils` `cn`).

import { useEffect, useMemo, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useTransform,
} from "framer-motion";
import {
  Braces,
  Briefcase,
  Building2,
  Check,
  Crown,
  FileText,
  Globe,
  HelpCircle,
  Home,
  Image as ImageIcon,
  Images,
  Landmark,
  Lock,
  MapPin,
  Megaphone,
  Network,
  Package,
  Plus,
  RotateCcw,
  Send,
  Shield,
  Sparkles,
  Star,
  Store,
  Trophy,
  User,
  Wrench,
  Zap,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Types + contract
// -----------------------------------------------------------------------------

export interface RawLevelFifteenState {
  // Paid toggles
  listingProtection: boolean;
  citations100: boolean;
  vaultCitations: boolean;
  premiumCitations: boolean;
  pressRelease: boolean;
  gbpServices: boolean;
  gbpPictureUpgrade: boolean; // $10/mo fallback for weekly photos
  basicSchema: boolean;
  superSchema: boolean;
  locationLandingPage: boolean;
  servicePages: boolean;
  locationPages: boolean;

  // Activity counters (reset monthly on your side)
  productLog: number; // target 3  (3/mo)
  pictureLog: number; // target 4  (1/wk)
  reviewLog: number;  // target 12 (3/wk)

  // Goal-achieved latches. Once a monthly goal is ever met, the latch flips
  // true permanently so the user keeps the +1 level even after the counter
  // resets next month. Host should set these whenever a counter crosses its
  // GOAL_TARGETS threshold.
  productGoalEverMet: boolean;
  pictureGoalEverMet: boolean;
  reviewGoalEverMet: boolean;
}

export interface LevelFifteenPowerUps {
  listingProtection: boolean;
  citations100: boolean;
  vaultCitations: boolean;
  premiumCitations: boolean;
  pressRelease: boolean;
  gbpProducts: boolean;
  gbpServices: boolean;
  gbpPictures: boolean;
  gbpReviews: boolean;
  basicSchema: boolean;
  superSchema: boolean;
  locationLandingPage: boolean;
  servicePages: boolean;
  locationPages: boolean;
}

export const GOAL_TARGETS = {
  productLog: 3,
  pictureLog: 4,
  reviewLog: 12,
} as const;

export function derivePowerUps(s: RawLevelFifteenState): LevelFifteenPowerUps {
  return {
    listingProtection: s.listingProtection,
    citations100: s.citations100,
    vaultCitations: s.vaultCitations,
    premiumCitations: s.premiumCitations,
    pressRelease: s.pressRelease,
    // Goals: latched flag OR current-month counter above threshold.
    gbpProducts:
      s.productGoalEverMet || s.productLog >= GOAL_TARGETS.productLog,
    gbpServices: s.gbpServices,
    gbpPictures:
      s.pictureGoalEverMet ||
      s.pictureLog >= GOAL_TARGETS.pictureLog ||
      s.gbpPictureUpgrade,
    gbpReviews:
      s.reviewGoalEverMet || s.reviewLog >= GOAL_TARGETS.reviewLog,
    basicSchema: s.basicSchema,
    superSchema: s.superSchema,
    locationLandingPage: s.locationLandingPage,
    servicePages: s.servicePages,
    locationPages: s.locationPages,
  };
}

/**
 * Maximum bonus levels earnable from the LVL 15 endgame panel.
 * 14 power-ups in LevelFifteenPowerUps + gbpPictureUpgrade counted separately
 * so users who pay AND hit the goal get both levels. Adjust if product
 * catalog changes.
 */
export const LEVEL_FIFTEEN_MAX_BONUS = 15;

/**
 * Count how many endgame power-ups are currently contributing to the level.
 * Picture goal + $10/mo upgrade are counted independently, so maxing out this
 * function returns LEVEL_FIFTEEN_MAX_BONUS.
 */
export function computeLevelFifteenBonus(
  state: RawLevelFifteenState,
  powerUps: LevelFifteenPowerUps,
): number {
  let n = 0;
  if (powerUps.listingProtection) n++;
  if (powerUps.citations100) n++;
  if (powerUps.vaultCitations) n++;
  if (powerUps.premiumCitations) n++;
  if (powerUps.pressRelease) n++;
  if (powerUps.gbpProducts) n++;
  if (powerUps.gbpServices) n++;
  if (powerUps.gbpPictures) n++;
  if (powerUps.gbpReviews) n++;
  if (powerUps.basicSchema) n++;
  if (powerUps.superSchema) n++;
  if (powerUps.locationLandingPage) n++;
  if (powerUps.servicePages) n++;
  if (powerUps.locationPages) n++;
  // Independent +1 if the user also paid for the picture upgrade on top of
  // hitting the picture goal organically.
  if (state.gbpPictureUpgrade && state.pictureGoalEverMet) n++;
  return Math.min(n, LEVEL_FIFTEEN_MAX_BONUS);
}

/**
 * Upgrade an old state blob that may be missing the new goal-latch fields.
 * Also auto-latches any goal whose counter is already above threshold, so
 * migrating users don't lose credit for goals they already hit.
 */
export function migrateLevelFifteenState(
  raw: Partial<RawLevelFifteenState>,
): RawLevelFifteenState {
  const s: RawLevelFifteenState = { ...INITIAL_LEVEL_FIFTEEN_STATE, ...raw };
  if (s.productLog >= GOAL_TARGETS.productLog) s.productGoalEverMet = true;
  if (s.pictureLog >= GOAL_TARGETS.pictureLog) s.pictureGoalEverMet = true;
  if (s.reviewLog >= GOAL_TARGETS.reviewLog) s.reviewGoalEverMet = true;
  return s;
}

export const INITIAL_LEVEL_FIFTEEN_STATE: RawLevelFifteenState = {
  listingProtection: false,
  citations100: false,
  vaultCitations: false,
  premiumCitations: false,
  pressRelease: false,
  gbpServices: false,
  gbpPictureUpgrade: false,
  basicSchema: false,
  superSchema: false,
  locationLandingPage: false,
  servicePages: false,
  locationPages: false,
  productLog: 0,
  pictureLog: 0,
  reviewLog: 0,
  productGoalEverMet: false,
  pictureGoalEverMet: false,
  reviewGoalEverMet: false,
};

interface DemoActions {
  reset: () => void;
  activate: () => void;
  toggleWebsite: () => void;
}

export interface GbpAnalysisData {
  photos: { count: number; thisMonth: number; lastAddedAt: string | null };
  reviews: { count: number; thisMonth: number; lastAddedAt: string | null };
  products: { count: number; thisMonth: number; lastAddedAt: string | null };
  postsEngine: { enabled: boolean; totalPosts: number };
  publishedPosts: { count: number; lastAt: string | null };
  scheduledPosts: { count: number; nextAt: string | null };
  socials: { connectedCount: number };
}

interface LevelFifteenViewProps {
  businessName: string;
  state: RawLevelFifteenState;
  powerUps: LevelFifteenPowerUps;
  hasWebsite: boolean;
  onPurchase: (productId: string) => void;
  onToggleUpgrade: (key: keyof RawLevelFifteenState) => void;
  demoActions?: DemoActions;
  /**
   * When true, suppress the internal header (title, business name, LVL 15
   * badge, demo actions) because the host page is already rendering its own
   * header chrome (e.g. Road-to-Domination). Default: false.
   */
  embedded?: boolean;
  /** Real GBP activity. When provided, Maintain goals (Products / Pictures /
   * Reviews) display real monthly counts and the manual "+ Log" button is hidden. */
  gbpAnalysis?: GbpAnalysisData;
}

// -----------------------------------------------------------------------------
// Tab groupings — 4-tab side panel, ordered by priority.
// Freely navigable; the numbers just indicate recommended purchase order.
// -----------------------------------------------------------------------------

export type LevelFifteenTabId = "fortify" | "amplify" | "expand" | "maintain";

export interface LevelFifteenTab {
  id: LevelFifteenTabId;
  label: string;
  subtitle: string;
  productIds: string[];
}

export const LEVEL_FIFTEEN_TABS: LevelFifteenTab[] = [
  {
    id: "fortify",
    label: "Fortify",
    subtitle: "Protect what's yours",
    productIds: ["listing-protection", "citations-100", "basic-schema"],
  },
  {
    id: "amplify",
    label: "Amplify",
    subtitle: "Boost your authority",
    productIds: [
      "vault-citations",
      "premium-citations",
      "gbp-services",
      "super-schema",
    ],
  },
  {
    id: "expand",
    label: "Expand",
    subtitle: "Build your web",
    productIds: ["location-landing-page", "service-pages", "location-pages"],
  },
  {
    id: "maintain",
    label: "Maintain",
    subtitle: "Keep the machine running",
    productIds: ["gbp-products", "gbp-pictures", "gbp-reviews", "press-release"],
  },
];

// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

const cn = (...args: Array<string | false | null | undefined>) =>
  args.filter(Boolean).join(" ");

// -----------------------------------------------------------------------------
// Product catalog
// -----------------------------------------------------------------------------

type AccentKey = "orange" | "teal" | "purple" | "amber" | "blue" | "emerald";

interface GoalFallback {
  title: string;
  description: string;
  priceLabel: string;
  toggleKey: keyof RawLevelFifteenState;
  actionLabel: string;
}
interface GoalCTA {
  title: string;
  description: string;
  actionLabel: string;
}
interface GoalSpec {
  target: number;
  counterKey: keyof RawLevelFifteenState;
  periodLabel: string;
  unit: string;
  logLabel: string;
  fallback?: GoalFallback;
  cta?: GoalCTA;
}
interface Product {
  id: string;
  title: string;
  tagline: string;
  dominates: string;
  priceLabel: string;
  accent: AccentKey;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  powers: keyof LevelFifteenPowerUps;
  goal?: GoalSpec;
  /** Optional marketplace route override. When set, the Buy button on this
   *  product navigates here instead of the default /marketplace/:id page. */
  route?: string;
}

const CORE_PRODUCTS: Product[] = [
  {
    id: "listing-protection",
    title: "Listing Protection",
    tagline: "24/7 Active Shield",
    dominates:
      "Monitors suspensions, unauthorized edits, and competitor attacks around the clock. Flips any threat before it costs a single grid point.",
    priceLabel: "$10/mo",
    accent: "amber",
    icon: Shield,
    powers: "listingProtection",
    route: "/listing",
  },
  {
    id: "citations-100",
    title: "Foothold Citations",
    tagline: "Get on the map",
    dominates:
      "100 business directory submissions to top-tier sites (Yelp, BBB, Bing, Apple Maps). One-time build with manual review and NAP consistency lock.",
    priceLabel: "$97",
    accent: "blue",
    icon: Zap,
    powers: "citations100",
    route: "/citations",
  },
  {
    id: "vault-citations",
    title: "Fortress Citations",
    tagline: "Defend the position",
    dominates:
      "150+ premium directory submissions with always-on monitoring, auto-detection of NAP changes, duplicate suppression, and annual refresh.",
    priceLabel: "$199/yr",
    accent: "emerald",
    icon: Sparkles,
    powers: "vaultCitations",
    route: "/citations",
  },
  {
    id: "premium-citations",
    title: "Empire Citations",
    tagline: "Rule the territory",
    dominates:
      "200+ directories including all Vault sites plus industry-specific and niche directories. Monthly health reports, priority support, auto-renewal.",
    priceLabel: "$399/yr",
    accent: "orange",
    icon: Crown,
    powers: "premiumCitations",
    route: "/citations",
  },
  {
    id: "press-release",
    title: "Custom Press Release",
    tagline: "Authority Boost",
    dominates:
      "Custom-written press release distributed across top news outlets. Instantly boosts GBP authority and drives fresh, high-trust backlinks to your website.",
    priceLabel: "$599",
    accent: "amber",
    icon: Megaphone,
    powers: "pressRelease",
    route: "/press-release",
  },
];

const GBP_EXPANSION: Product[] = [
  {
    id: "gbp-products",
    title: "Products",
    tagline: "Monthly Activity",
    dominates:
      "Keep your listing fresh by adding at least 3 new products per month. Consistent updates signal an active business to Google.",
    priceLabel: "Goal: 3 / month",
    accent: "orange",
    icon: Package,
    powers: "gbpProducts",
    goal: {
      target: 3,
      counterKey: "productLog",
      periodLabel: "this month",
      unit: "products",
      logLabel: "Log product",
    },
  },
  {
    id: "gbp-services",
    title: "Services",
    tagline: "Service Coverage",
    dominates:
      "Add 100 service entries to your listing. Every search term you want to rank for becomes possible — breadth competitors can't match.",
    priceLabel: "+100 for $49",
    accent: "orange",
    icon: Wrench,
    powers: "gbpServices",
    route: "/services",
  },
  {
    id: "gbp-pictures",
    title: "Pictures",
    tagline: "Weekly Activity",
    dominates:
      "Upload at least one photo per week to keep your listing fresh. Visual density signals an active, trustworthy business to Google.",
    priceLabel: "Goal: 1 / week",
    accent: "orange",
    icon: Images,
    powers: "gbpPictures",
    goal: {
      target: 4,
      counterKey: "pictureLog",
      periodLabel: "this month (1 / week)",
      unit: "photos",
      logLabel: "Log photo",
      fallback: {
        title: "GPS-Optimized Pictures",
        description:
          "Not hitting your weekly goal? We'll upload 10 geo-tagged pictures for you this month.",
        priceLabel: "$10 / mo",
        toggleKey: "gbpPictureUpgrade",
        actionLabel: "Activate upgrade",
      },
    },
  },
  {
    id: "gbp-reviews",
    title: "Reviews",
    tagline: "Social Proof Engine",
    dominates:
      "Aim for at least 3 new reviews per week. Compounding 5-stars flip customer decisions and keep your Local Pack position.",
    priceLabel: "Goal: 3 / week",
    accent: "orange",
    icon: Star,
    powers: "gbpReviews",
    goal: {
      target: 12,
      counterKey: "reviewLog",
      periodLabel: "this month (3 / week)",
      unit: "reviews",
      logLabel: "Log review",
      cta: {
        title: "Behind on reviews?",
        description:
          "Send a review request to past clients who haven't left one yet.",
        actionLabel: "Request reviews",
      },
    },
  },
];

const SCHEMA_PRODUCTS: Product[] = [
  {
    id: "basic-schema",
    title: "Basic Schema",
    tagline: "LocalBusiness JSON-LD",
    dominates:
      "A single LocalBusiness JSON-LD block (name, address, phone, description) installed sitewide. The minimum Google needs to recognize your business as a local entity.",
    priceLabel: "Free",
    accent: "purple",
    icon: Braces,
    powers: "basicSchema",
    route: "/schema",
  },
  {
    id: "super-schema",
    title: "Super Schema",
    tagline: "Full Entity Graph",
    dominates:
      "Adds the other seven entity types — WebPage, WebSite, Organization, Services, FAQ, ImageObject, Person — all interlinked via @graph. Forms one cohesive knowledge graph Google can fully understand.",
    priceLabel: "$999 one-time",
    accent: "purple",
    icon: Network,
    powers: "superSchema",
    route: "/schema",
  },
];

const WEBSITE_EXPANSION: Product[] = [
  {
    id: "location-landing-page",
    title: "Location Landing Page",
    tagline: "Flagship Location Page",
    dominates:
      "A conversion-tuned landing page for your primary location, purpose-built to rank for service-near-me queries and anchor your Maps Domination.",
    priceLabel: "$199",
    accent: "teal",
    icon: Home,
    powers: "locationLandingPage",
  },
  {
    id: "service-pages",
    title: "Service Pages",
    tagline: "Service Coverage",
    dominates:
      "Dedicated SEO pages for every service you offer — each one capturing high-intent search traffic and funneling qualified leads into your intake pipeline.",
    priceLabel: "$49/page",
    accent: "teal",
    icon: Briefcase,
    powers: "servicePages",
  },
  {
    id: "location-pages",
    title: "Location Pages",
    tagline: "Multi-Location Scale",
    dominates:
      "Geo-targeted pages for every neighborhood or service area you want to dominate. Each page is tuned to flip local rankings in its target zone.",
    priceLabel: "$49/page",
    accent: "teal",
    icon: MapPin,
    powers: "locationPages",
  },
];

export const LEVEL_FIFTEEN_PRODUCTS: Product[] = [
  ...CORE_PRODUCTS,
  ...GBP_EXPANSION,
  ...SCHEMA_PRODUCTS,
  ...WEBSITE_EXPANSION,
];

const accentStyles: Record<
  AccentKey,
  { text: string; bg: string; glow: string; shadowBase: string }
> = {
  orange: {
    text: "text-orange-400",
    bg: "bg-orange-500/10",
    glow: "shadow-[0_0_50px_rgba(251,146,60,0.45)]",
    shadowBase: "251,146,60",
  },
  teal: {
    text: "text-teal-300",
    bg: "bg-teal-500/10",
    glow: "shadow-[0_0_50px_rgba(94,234,212,0.45)]",
    shadowBase: "94,234,212",
  },
  purple: {
    text: "text-purple-400",
    bg: "bg-purple-500/10",
    glow: "shadow-[0_0_50px_rgba(168,85,247,0.45)]",
    shadowBase: "168,85,247",
  },
  amber: {
    text: "text-amber-300",
    bg: "bg-amber-500/10",
    glow: "shadow-[0_0_50px_rgba(252,211,77,0.5)]",
    shadowBase: "252,211,77",
  },
  blue: {
    text: "text-blue-400",
    bg: "bg-blue-500/10",
    glow: "shadow-[0_0_50px_rgba(59,130,246,0.45)]",
    shadowBase: "59,130,246",
  },
  emerald: {
    text: "text-emerald-300",
    bg: "bg-emerald-500/10",
    glow: "shadow-[0_0_50px_rgba(16,185,129,0.45)]",
    shadowBase: "16,185,129",
  },
};

// -----------------------------------------------------------------------------
// Main view
// -----------------------------------------------------------------------------

export function LevelFifteenView({
  businessName,
  state,
  powerUps,
  hasWebsite,
  onPurchase,
  onToggleUpgrade,
  demoActions,
  embedded = false,
  gbpAnalysis,
}: LevelFifteenViewProps) {
  const totalCount = Object.keys(powerUps).length;
  const activeCount = Object.values(powerUps).filter(Boolean).length;

  const [activeTabId, setActiveTabId] = useState<LevelFifteenTabId>("fortify");

  // Build a product-id → Product lookup for the tabbed side panel.
  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of LEVEL_FIFTEEN_PRODUCTS) m.set(p.id, p);
    return m;
  }, []);

  // How many products in each tab are active — used for per-tab progress pips.
  const tabProgress = useMemo(() => {
    const out: Record<LevelFifteenTabId, { active: number; total: number }> = {
      fortify: { active: 0, total: 0 },
      amplify: { active: 0, total: 0 },
      expand: { active: 0, total: 0 },
      maintain: { active: 0, total: 0 },
    };
    for (const tab of LEVEL_FIFTEEN_TABS) {
      for (const pid of tab.productIds) {
        const p = productById.get(pid);
        if (!p) continue;
        out[tab.id].total += 1;
        if (powerUps[p.powers]) out[tab.id].active += 1;
      }
    }
    return out;
  }, [powerUps, productById]);

  const activeTab =
    LEVEL_FIFTEEN_TABS.find((t) => t.id === activeTabId) ?? LEVEL_FIFTEEN_TABS[0];
  const activeTabProducts = activeTab.productIds
    .map((id) => productById.get(id))
    .filter((p): p is Product => !!p);

  return (
    <div className="relative flex h-full w-full flex-col bg-[#0b0b0f] text-white">
      {!embedded && (
        <header className="flex items-center justify-between border-b border-white/5 px-8 py-5">
          <div className="flex items-center gap-4">
            <span className="text-2xl font-semibold tracking-tight text-amber-300">
              Road to Domination
            </span>
            <span className="h-6 w-px bg-white/10" />
            <span className="text-sm uppercase tracking-[0.2em] text-white/50">
              {businessName}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Sparkles className="h-4 w-4 text-amber-300" />
              <span>Power-ups active</span>
              <span className="font-semibold text-white">
                {activeCount}/{totalCount}
              </span>
            </div>
            <div className="rounded border border-amber-300/40 bg-amber-300/5 px-3 py-1 text-sm font-semibold tracking-widest text-amber-300">
              LVL 15
            </div>
            {demoActions && (
              <div className="flex items-center gap-2 border-l border-white/10 pl-4">
                <button
                  onClick={demoActions.toggleWebsite}
                  className={cn(
                    "flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs uppercase tracking-widest transition-colors",
                    hasWebsite
                      ? "border-teal-300/30 bg-teal-300/10 text-teal-200 hover:bg-teal-300/20"
                      : "border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/10",
                  )}
                >
                  <Globe className="h-3 w-3" />{" "}
                  {hasWebsite ? "Has website" : "No website"}
                </button>
                <button
                  onClick={demoActions.reset}
                  className="flex items-center gap-1.5 rounded border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs uppercase tracking-widest text-white/60 hover:bg-white/10"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
                <button
                  onClick={demoActions.activate}
                  className="flex items-center gap-1.5 rounded border border-amber-300/30 bg-amber-300/10 px-2.5 py-1 text-xs uppercase tracking-widest text-amber-300 hover:bg-amber-300/20"
                >
                  <Zap className="h-3 w-3" /> Activate all
                </button>
              </div>
            )}
          </div>
        </header>
      )}

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden md:flex w-full md:w-[380px] md:shrink-0 flex-col overflow-hidden border-r border-white/5">
          {/* Panel header */}
          <div className="shrink-0 border-b border-white/5 px-5 pb-4 pt-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-white">
                Forge &amp; Expand
              </h2>
              <div className="flex items-center gap-1.5 rounded border border-amber-300/30 bg-amber-300/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-300">
                <Sparkles className="h-3 w-3" />
                {activeCount}/{totalCount}
              </div>
            </div>
            <p className="mt-1 text-xs text-white/50">
              Four-phase endgame. Buy in order, or jump wherever you like.
            </p>
          </div>

          {/* Tab bar */}
          <LevelFifteenTabBar
            tabs={LEVEL_FIFTEEN_TABS}
            activeId={activeTabId}
            onChange={setActiveTabId}
            progress={tabProgress}
          />

          {/* Active tab body */}
          <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-4 pt-3">
            <div className="mb-3 flex items-baseline justify-between">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50">
                {activeTab.subtitle}
              </p>
              <span className="text-[11px] font-semibold text-white/70">
                {tabProgress[activeTab.id].active}/
                {tabProgress[activeTab.id].total}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {activeTabProducts.map((product) => (
                <CompactProductCard
                  key={product.id}
                  product={product}
                  active={powerUps[product.powers]}
                  state={state}
                  onPurchase={() => onPurchase(product.id)}
                  onToggleUpgrade={onToggleUpgrade}
                  gbpAnalysis={gbpAnalysis}
                />
              ))}
            </div>
          </div>
        </aside>

        <section className="relative flex-1 overflow-auto">
          <AmbientGlow />
          <TrinityStage powerUps={powerUps} hasWebsite={hasWebsite} />
        </section>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab bar — numbered tabs in Road-to-Domination style
// -----------------------------------------------------------------------------

interface LevelFifteenTabBarProps {
  tabs: LevelFifteenTab[];
  activeId: LevelFifteenTabId;
  onChange: (id: LevelFifteenTabId) => void;
  progress: Record<LevelFifteenTabId, { active: number; total: number }>;
}

function LevelFifteenTabBar({
  tabs,
  activeId,
  onChange,
  progress,
}: LevelFifteenTabBarProps) {
  return (
    <div className="shrink-0 border-b border-white/5 bg-white/[0.015] px-2 py-3">
      <div className="grid grid-cols-4 gap-1">
        {tabs.map((tab, idx) => {
          const p = progress[tab.id];
          const complete = p.total > 0 && p.active >= p.total;
          const isActive = activeId === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={cn(
                "group flex flex-col items-center gap-1.5 rounded-md px-1.5 py-2 transition-colors",
                isActive
                  ? "bg-amber-300/10"
                  : "hover:bg-white/[0.04]",
              )}
            >
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-bold transition-colors",
                  complete
                    ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-300"
                    : isActive
                      ? "border-amber-300/60 bg-amber-300/15 text-amber-300"
                      : "border-white/15 bg-white/[0.04] text-white/60",
                )}
              >
                {complete ? <Check className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              <span
                className={cn(
                  "text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors",
                  isActive
                    ? "text-amber-300"
                    : complete
                      ? "text-emerald-300/90"
                      : "text-white/55 group-hover:text-white/80",
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Compact product card — Road-to-Domination-style one-line row
// -----------------------------------------------------------------------------

interface CompactProductCardProps {
  product: Product;
  active: boolean;
  state: RawLevelFifteenState;
  onPurchase: () => void;
  onToggleUpgrade: (key: keyof RawLevelFifteenState) => void;
  gbpAnalysis?: GbpAnalysisData;
}

function CompactProductCard({
  product,
  active,
  state,
  onPurchase,
  onToggleUpgrade,
  gbpAnalysis,
}: CompactProductCardProps) {
  const a = accentStyles[product.accent];
  const Icon = product.icon;
  const isGoal = !!product.goal;
  const counterKey = isGoal ? product.goal!.counterKey : null;
  // Products / Pictures / Reviews are ALWAYS GBP-driven (read-only). The
  // count comes from real Google Business Profile activity for the current
  // calendar month. Manual click-logging is disabled — the "+" button is
  // hidden — so users can't inflate the goal. Until gbpAnalysis loads, count
  // shows 0 for these (we intentionally do NOT fall back to stale local
  // counters that may be left over from the old click-log behavior).
  const gbpBacked: boolean = (
    counterKey === "productLog" || counterKey === "pictureLog" || counterKey === "reviewLog"
  );
  const gbpCount: number = gbpBacked && gbpAnalysis ? (
    counterKey === "productLog" ? gbpAnalysis.products.thisMonth :
    counterKey === "pictureLog" ? gbpAnalysis.photos.thisMonth :
    counterKey === "reviewLog"  ? gbpAnalysis.reviews.thisMonth : 0
  ) : 0;
  const count = isGoal
    ? (gbpBacked ? gbpCount : (state[product.goal!.counterKey] as number) || 0)
    : 0;
  const target = isGoal ? product.goal!.target : 0;
  const goalMet = isGoal && count >= target;
  const progressPct = isGoal
    ? Math.min(100, Math.round((count / target) * 100))
    : 0;
  const upgradeActive =
    isGoal &&
    !!product.goal!.fallback &&
    !!state[product.goal!.fallback.toggleKey];

  return (
    <div
      className={cn(
        "group flex flex-col gap-2 rounded-lg border px-3 py-2.5 transition-all",
        active
          ? "border-emerald-400/30 bg-emerald-400/[0.04]"
          : "border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]",
      )}
    >
      <div className="flex items-center gap-2.5">
        {/* Checkbox / status */}
        <div
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border transition-colors",
            active
              ? "border-emerald-400/70 bg-emerald-400/20 text-emerald-300"
              : cn("border-white/15", a.text),
          )}
        >
          {active ? (
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          ) : (
            <Icon className="h-3 w-3" />
          )}
        </div>

        {/* Title + tagline */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-[13px] font-semibold text-white">
              {product.title}
            </h3>
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.12em]",
                a.bg,
                a.text,
              )}
            >
              {product.priceLabel}
            </span>
          </div>
          <p className="truncate text-[11px] text-white/55">{product.tagline}</p>
        </div>

        {/* Action button */}
        {isGoal && gbpBacked ? (
          <span
            data-testid={`gbp-badge-${product.id}`}
            title="Pulled live from your Google Business Profile"
            className="shrink-0 rounded border border-blue-400/30 bg-blue-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-blue-300"
          >
            GBP
          </span>
        ) : isGoal ? (
          <button
            onClick={onPurchase}
            className="shrink-0 rounded border border-white/15 bg-white/[0.04] px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/75 transition-colors hover:border-brand-orange/40 hover:bg-brand-orange/10 hover:text-brand-orange"
          >
            <Plus className="h-3 w-3" />
          </button>
        ) : !active ? (
          <button
            onClick={onPurchase}
            className={cn(
              "shrink-0 rounded border border-brand-orange/40 bg-brand-orange/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-orange transition-colors hover:bg-brand-orange/25",
            )}
          >
            Buy
          </button>
        ) : (
          <span className="shrink-0 rounded border border-emerald-400/40 bg-emerald-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-300">
            Active
          </span>
        )}
      </div>

      {/* Inline goal progress bar */}
      {isGoal && product.goal && (
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/5">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                goalMet ? "bg-emerald-400" : "bg-amber-300",
              )}
              style={{
                width: `${progressPct}%`,
                boxShadow: goalMet
                  ? "0 0 8px rgba(52,211,153,0.5)"
                  : "0 0 8px rgba(252,211,77,0.4)",
              }}
            />
          </div>
          <span
            className={cn(
              "shrink-0 text-[10px] font-semibold tabular-nums",
              goalMet ? "text-emerald-300" : "text-white/60",
            )}
          >
            {count}/{target}
          </span>
          {product.goal.fallback && !goalMet && (
            <button
              onClick={() =>
                onToggleUpgrade(product.goal!.fallback!.toggleKey)
              }
              className={cn(
                "shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest transition-colors",
                upgradeActive
                  ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                  : "border-white/15 bg-white/[0.04] text-white/60 hover:border-brand-orange/40 hover:text-brand-orange",
              )}
              title={product.goal.fallback.title}
            >
              {upgradeActive ? "Covered" : "+$10/mo"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Side panel: section heading + product card
// -----------------------------------------------------------------------------

function SectionHeading({ label }: { label: string }) {
  return (
    <div className="mb-3 mt-6 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/40">
      <span className="h-px flex-1 bg-white/10" />
      <span>{label}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

interface ProductCardProps {
  product: Product;
  active: boolean;
  state: RawLevelFifteenState;
  onPurchase: () => void;
  onToggleUpgrade: (key: keyof RawLevelFifteenState) => void;
}

function ProductCard({
  product,
  active,
  state,
  onPurchase,
  onToggleUpgrade,
}: ProductCardProps) {
  const a = accentStyles[product.accent];
  const Icon = product.icon;
  const isGoal = !!product.goal;
  const count = isGoal
    ? (state[product.goal!.counterKey] as number) || 0
    : 0;
  const target = isGoal ? product.goal!.target : 0;
  const goalMet = isGoal && count >= target;
  const progressPct = isGoal
    ? Math.min(100, Math.round((count / target) * 100))
    : 0;
  const upgradeActive =
    isGoal &&
    !!product.goal!.fallback &&
    !!state[product.goal!.fallback.toggleKey];

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-4 transition-all",
        active && "border-white/20 bg-white/[0.04]",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg border border-white/10",
            active ? cn(a.bg, a.text) : "bg-white/5 text-white/30",
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">{product.title}</h3>
            {active && (
              <span className="flex h-5 items-center gap-1 rounded border border-emerald-400/40 bg-emerald-400/10 px-1.5 text-[10px] uppercase tracking-widest text-emerald-300">
                <Check className="h-3 w-3" />{" "}
                {isGoal
                  ? upgradeActive && !goalMet
                    ? "Covered"
                    : "Goal Met"
                  : "Active"}
              </span>
            )}
          </div>
          <p
            className={cn("text-[11px] uppercase tracking-[0.15em]", a.text)}
          >
            {product.tagline}
          </p>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-white/60">
        {product.dominates}
      </p>

      {isGoal && product.goal ? (
        <div className="flex flex-col gap-2 border-t border-white/5 pt-3">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.15em]">
            <span className="text-white/50">{product.goal.periodLabel}</span>
            <span
              className={cn(
                "font-semibold",
                goalMet ? "text-emerald-300" : "text-white/80",
              )}
            >
              {count} / {target} {product.goal.unit}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                goalMet ? "bg-emerald-400" : "bg-amber-300",
              )}
              style={{
                width: `${progressPct}%`,
                boxShadow: goalMet
                  ? "0 0 10px rgba(52,211,153,0.6)"
                  : "0 0 10px rgba(252,211,77,0.5)",
              }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">
              {product.priceLabel}
            </span>
            <button
              onClick={onPurchase}
              className="flex items-center gap-1 rounded bg-brand-orange px-3 h-8 text-xs font-semibold uppercase tracking-widest text-white hover:bg-brand-orange/90"
            >
              <Plus className="h-3 w-3" /> {product.goal.logLabel}
            </button>
          </div>

          {product.goal.fallback && !goalMet && (
            <div className="mt-1 flex flex-col gap-2 rounded-md border border-amber-300/20 bg-amber-300/[0.04] p-3">
              <div className="flex items-center gap-2">
                <Images className="h-4 w-4 text-amber-300" />
                <span className="text-xs font-semibold text-amber-200">
                  {product.goal.fallback.title}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-white/60">
                {product.goal.fallback.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-white">
                  {product.goal.fallback.priceLabel}
                </span>
                <button
                  onClick={() =>
                    onToggleUpgrade(product.goal!.fallback!.toggleKey)
                  }
                  className={cn(
                    "h-7 rounded px-2.5 text-[10px] font-semibold uppercase tracking-widest transition-colors",
                    upgradeActive
                      ? "border border-white/10 bg-white/10 text-white/80 hover:bg-white/15"
                      : "bg-brand-orange text-white hover:bg-brand-orange/90",
                  )}
                >
                  {upgradeActive
                    ? "Active"
                    : product.goal.fallback.actionLabel}
                </button>
              </div>
            </div>
          )}

          {product.goal.cta && !goalMet && (
            <div className="mt-1 flex flex-col gap-2 rounded-md border border-amber-300/20 bg-amber-300/[0.04] p-3">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-amber-300" />
                <span className="text-xs font-semibold text-amber-200">
                  {product.goal.cta.title}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-white/60">
                {product.goal.cta.description}
              </p>
              <button
                onClick={onPurchase}
                className="h-7 rounded bg-brand-orange px-2.5 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-brand-orange/90"
              >
                {product.goal.cta.actionLabel}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between border-t border-white/5 pt-3">
          <span className="text-sm font-semibold text-white">
            {product.priceLabel}
          </span>
          <button
            onClick={onPurchase}
            className={cn(
              "h-8 rounded px-3 text-xs font-semibold uppercase tracking-widest transition-colors",
              active
                ? "border border-white/10 bg-white/10 text-white/80 hover:bg-white/15"
                : "bg-brand-orange text-white hover:bg-brand-orange/90",
            )}
          >
            {active ? "Deactivate" : "Activate"}
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Stage + geometry
// -----------------------------------------------------------------------------

function AmbientGlow() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div
        className="absolute left-1/2 top-1/2 h-[820px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(251,146,60,0.10) 0%, rgba(168,85,247,0.06) 40%, rgba(94,234,212,0.10) 70%, transparent 100%)",
        }}
      />
    </div>
  );
}

const STAGE_W = 750;
const STAGE_H = 780;
const NODE = 112;
const SUB_NODE = 72;

// Equilateral trinity: side length 360. Centroid at (380, 456).
const POS = {
  gbp: { x: 380, y: 248 },
  website: { x: 200, y: 560 },
  schema: { x: 560, y: 560 },
};

// GBP sub-nodes arc above GBP.
const GBP_SUB_POS = {
  products: { x: 253, y: 189 },
  services: { x: 310, y: 127 },
  pictures: { x: 450, y: 127 },
  reviews: { x: 507, y: 189 },
};
const WEBSITE_SUB_POS = {
  landingPage: { x: 70, y: 450 },
  servicePages: { x: 50, y: 560 },
  locationPages: { x: 70, y: 670 },
};
const SCHEMA_SUB_POS = {
  webPage: { x: 655, y: 465 },
  webSite: { x: 690, y: 525 },
  organization: { x: 690, y: 595 },
  localBusiness: { x: 655, y: 655 },
  services: { x: 595, y: 690 },
  faq: { x: 525, y: 690 },
  imageObject: { x: 465, y: 655 },
  person: { x: 430, y: 595 },
};

// Citation cluster: upper-left of trinity.
const CITATION_GROUP_CENTER = { x: 130, y: 270 };
const CITATION_SUB_POS = {
  citations100: { x: 130, y: 170 },
  vaultCitations: { x: 50, y: 325 },
  premiumCitations: { x: 210, y: 325 },
};
const CITATION_EDGE_COLORS = {
  citations100: "#3b82f6",
  vaultCitations: "#10b981",
  premiumCitations: "#fb923c",
};

const PRESS_RELEASE_POS = { x: 290, y: 360 };
const PRESS_RELEASE_EDGE_COLOR = "#fcd34d";

const TRINITY_CENTROID = { x: 380, y: 456 };

function TrinityStage({
  powerUps,
  hasWebsite,
}: {
  powerUps: LevelFifteenPowerUps;
  hasWebsite: boolean;
}) {
  const anyCitation =
    powerUps.citations100 ||
    powerUps.vaultCitations ||
    powerUps.premiumCitations;
  const allCitations =
    powerUps.citations100 &&
    powerUps.vaultCitations &&
    powerUps.premiumCitations;
  const anyWebsiteExpansion =
    powerUps.locationLandingPage ||
    powerUps.servicePages ||
    powerUps.locationPages;
  const websitePowered = hasWebsite || anyCitation || anyWebsiteExpansion;
  const schemaPowered = powerUps.basicSchema || powerUps.superSchema;
  const showGraphLinks = powerUps.superSchema;

  // Basic Schema lights LocalBusiness only; Super Schema lights the other 7.
  const schemaSubs = {
    webPage: powerUps.superSchema,
    webSite: powerUps.superSchema,
    organization: powerUps.superSchema,
    localBusiness: powerUps.basicSchema,
    services: powerUps.superSchema,
    faq: powerUps.superSchema,
    imageObject: powerUps.superSchema,
    person: powerUps.superSchema,
  };

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative"
        style={{ width: STAGE_W, height: STAGE_H }}
      >
        <TrinityConnections
          powerUps={powerUps}
          allCitations={allCitations}
          websitePowered={websitePowered}
          schemaPowered={schemaPowered}
          showGraphLinks={showGraphLinks}
          schemaSubs={schemaSubs}
        />

        <AnimatePresence>
          {anyCitation && <EnergyCore key="energy" powerUps={powerUps} />}
        </AnimatePresence>

        <CitationGroupAura active={anyCitation} allActive={allCitations} />

        <AnimatePresence>
          {allCitations && <TotalDominationShockwave key="shockwave" />}
        </AnimatePresence>

        <AnimatePresence>
          {allCitations && <TotalDominationCenterpiece key="td-center" />}
        </AnimatePresence>

        <TrinityNode
          center={POS.gbp}
          label="GBP"
          sublabel="Google Business Profile"
          accent="orange"
          icon={Building2}
          powered
          shielded={powerUps.listingProtection}
          delay={0}
        />
        <TrinityNode
          center={POS.website}
          label="Website"
          sublabel="Authority Hub"
          accent="teal"
          icon={Globe}
          powered={websitePowered}
          delay={0.15}
        />
        <TrinityNode
          center={POS.schema}
          label="Schema"
          sublabel="Entity Signals"
          accent="purple"
          icon={Braces}
          powered={schemaPowered}
          delay={0.3}
        />

        <SubNode center={CITATION_SUB_POS.citations100}     label="Foothold"      icon={Zap}      accent="blue"    powered={powerUps.citations100}     delay={0.42} />
        <SubNode center={CITATION_SUB_POS.vaultCitations}   label="Fortress"      icon={Sparkles} accent="emerald" powered={powerUps.vaultCitations}   delay={0.5} />
        <SubNode center={CITATION_SUB_POS.premiumCitations} label="Empire"        icon={Crown}    accent="orange"  powered={powerUps.premiumCitations} delay={0.58} />

        <SubNode center={PRESS_RELEASE_POS} label="Press Release" icon={Megaphone} accent="amber" powered={powerUps.pressRelease} delay={0.62} />

        <SubNode center={GBP_SUB_POS.products} label="Products" icon={Package} accent="orange" powered={powerUps.gbpProducts} delay={0.4} />
        <SubNode center={GBP_SUB_POS.services} label="Services" icon={Wrench}  accent="orange" powered={powerUps.gbpServices} delay={0.48} />
        <SubNode center={GBP_SUB_POS.pictures} label="Pictures" icon={Images}  accent="orange" powered={powerUps.gbpPictures} delay={0.56} />
        <SubNode center={GBP_SUB_POS.reviews}  label="Reviews"  icon={Star}    accent="orange" powered={powerUps.gbpReviews}  delay={0.64} />

        <SubNode center={WEBSITE_SUB_POS.landingPage}   label="Landing Page"   icon={Home}      accent="teal" powered={powerUps.locationLandingPage} delay={0.45} />
        <SubNode center={WEBSITE_SUB_POS.servicePages}  label="Service Pages"  icon={Briefcase} accent="teal" powered={powerUps.servicePages}        delay={0.55} />
        <SubNode center={WEBSITE_SUB_POS.locationPages} label="Location Pages" icon={MapPin}    accent="teal" powered={powerUps.locationPages}       delay={0.65} />

        <SubNode center={SCHEMA_SUB_POS.webPage}       label="WebPage"       icon={FileText}   accent="purple" powered={schemaSubs.webPage}       delay={0.42} />
        <SubNode center={SCHEMA_SUB_POS.webSite}       label="WebSite"       icon={Network}    accent="purple" powered={schemaSubs.webSite}       delay={0.48} />
        <SubNode center={SCHEMA_SUB_POS.organization}  label="Organization"  icon={Landmark}   accent="purple" powered={schemaSubs.organization}  delay={0.54} />
        <SubNode center={SCHEMA_SUB_POS.localBusiness} label="LocalBusiness" icon={Store}      accent="purple" powered={schemaSubs.localBusiness} delay={0.6} />
        <SubNode center={SCHEMA_SUB_POS.services}      label="Services"      icon={Wrench}     accent="purple" powered={schemaSubs.services}      delay={0.66} />
        <SubNode center={SCHEMA_SUB_POS.faq}           label="FAQ"           icon={HelpCircle} accent="purple" powered={schemaSubs.faq}           delay={0.72} />
        <SubNode center={SCHEMA_SUB_POS.imageObject}   label="ImageObject"   icon={ImageIcon}  accent="purple" powered={schemaSubs.imageObject}   delay={0.78} />
        <SubNode center={SCHEMA_SUB_POS.person}        label="Person"        icon={User}       accent="purple" powered={schemaSubs.person}        delay={0.84} />
      </motion.div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Visual effects
// -----------------------------------------------------------------------------

function EnergyCore({ powerUps }: { powerUps: LevelFifteenPowerUps }) {
  const activeCount = Object.values(powerUps).filter(Boolean).length;
  const totalCount = Object.keys(powerUps).length;
  const intensity = Math.min(1, activeCount / Math.max(1, totalCount));
  const size = 90 + intensity * 200;
  const innerOpacity = 0.4 + intensity * 0.6;
  const haloOpacity = 0.22 + intensity * 0.55;
  const { x: cx, y: cy } = TRINITY_CENTROID;
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0, scale: 0.4 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.4 }}
      transition={{ duration: 0.65, ease: "easeOut" }}
      className="pointer-events-none absolute"
      style={{
        left: cx - size / 2,
        top: cy - size / 2,
        width: size,
        height: size,
      }}
    >
      <motion.div
        className="absolute inset-0"
        animate={{ scale: [0.94, 1.08, 0.94] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <div
          className="absolute inset-0 rounded-full blur-2xl"
          style={{
            background:
              "radial-gradient(circle, rgba(251,146,60,1) 0%, rgba(168,85,247,0.6) 40%, rgba(94,234,212,0.4) 70%, transparent 100%)",
            opacity: haloOpacity,
          }}
        />
        <div
          className="absolute inset-[22%] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(252,211,77,0.7) 30%, rgba(251,146,60,0.3) 65%, transparent 90%)",
            opacity: innerOpacity,
          }}
        />
      </motion.div>
    </motion.div>
  );
}

function CitationGroupAura({
  active,
  allActive,
}: {
  active: boolean;
  allActive: boolean;
}) {
  if (!active) return null;
  const size = 260;
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{ opacity: allActive ? 1 : 0.55 }}
      transition={{ duration: 0.6 }}
      className="pointer-events-none absolute"
      style={{
        left: CITATION_GROUP_CENTER.x - size / 2,
        top: CITATION_GROUP_CENTER.y - size / 2,
        width: size,
        height: size,
      }}
    >
      <motion.div
        animate={{ scale: [0.96, 1.04, 0.96] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 rounded-full"
        style={{
          background: allActive
            ? "radial-gradient(circle, rgba(252,211,77,0.22) 0%, rgba(168,85,247,0.14) 45%, rgba(251,146,60,0.10) 70%, transparent 100%)"
            : "radial-gradient(circle, rgba(94,234,212,0.10) 0%, rgba(168,85,247,0.06) 60%, transparent 100%)",
          filter: "blur(12px)",
        }}
      />
      <div
        className="absolute inset-0 rounded-full border"
        style={{
          borderColor: allActive
            ? "rgba(252,211,77,0.35)"
            : "rgba(255,255,255,0.08)",
          boxShadow: allActive
            ? "0 0 40px rgba(252,211,77,0.25), inset 0 0 30px rgba(168,85,247,0.15)"
            : "none",
        }}
      />
    </motion.div>
  );
}

function TotalDominationCenterpiece() {
  const cx = CITATION_GROUP_CENTER.x;
  const cy = CITATION_GROUP_CENTER.y;
  const W = 170;
  const H = 120;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
      className="pointer-events-none absolute flex flex-col items-center justify-center"
      style={{ left: cx - W / 2, top: cy - H / 2, width: W, height: H }}
    >
      <motion.div
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="flex h-14 w-14 items-center justify-center rounded-full border"
        style={{
          background:
            "linear-gradient(135deg, rgba(168,85,247,0.35), rgba(252,211,77,0.35), rgba(251,146,60,0.35))",
          borderColor: "rgba(252,211,77,0.7)",
          boxShadow:
            "0 0 32px rgba(252,211,77,0.6), 0 0 60px rgba(168,85,247,0.4)",
        }}
      >
        <Trophy className="h-7 w-7 text-amber-200" strokeWidth={1.8} />
      </motion.div>
      <div className="mt-2 text-center leading-tight">
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-200"
          style={{ textShadow: "0 0 10px rgba(252,211,77,0.7)" }}
        >
          Total
        </div>
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-200"
          style={{ textShadow: "0 0 10px rgba(252,211,77,0.7)" }}
        >
          Domination
        </div>
      </div>
    </motion.div>
  );
}

function TotalDominationShockwave() {
  const { x: cx, y: cy } = TRINITY_CENTROID;
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0.8, scale: 0.2 }}
      animate={{ opacity: 0, scale: 3.2 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: "easeOut" }}
      className="pointer-events-none absolute rounded-full"
      style={{
        left: cx - 180,
        top: cy - 180,
        width: 360,
        height: 360,
        border: "2px solid rgba(252,211,77,0.7)",
        boxShadow:
          "0 0 60px rgba(252,211,77,0.55), inset 0 0 60px rgba(251,146,60,0.35)",
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// SVG edges + graph links
// -----------------------------------------------------------------------------

const GRAPH_LINK_PAIRS: Array<
  [keyof typeof SCHEMA_SUB_POS, keyof typeof SCHEMA_SUB_POS]
> = [
  ["webPage", "webSite"],
  ["webSite", "organization"],
  ["organization", "localBusiness"],
  ["localBusiness", "services"],
  ["services", "faq"],
  ["faq", "imageObject"],
  ["imageObject", "person"],
  ["person", "webPage"],
  ["webSite", "localBusiness"],
  ["organization", "services"],
  ["faq", "person"],
];

function TrinityConnections({
  powerUps,
  allCitations,
  websitePowered,
  schemaPowered,
  showGraphLinks,
  schemaSubs,
}: {
  powerUps: LevelFifteenPowerUps;
  allCitations: boolean;
  websitePowered: boolean;
  schemaPowered: boolean;
  showGraphLinks: boolean;
  schemaSubs: Record<keyof typeof SCHEMA_SUB_POS, boolean>;
}) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
      fill="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="edge-gbp-website" x1={POS.gbp.x} y1={POS.gbp.y} x2={POS.website.x} y2={POS.website.y} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fb923c" stopOpacity="0.9" />
          <stop offset="1" stopColor="#5eead4" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="edge-gbp-schema" x1={POS.gbp.x} y1={POS.gbp.y} x2={POS.schema.x} y2={POS.schema.y} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fb923c" stopOpacity="0.9" />
          <stop offset="1" stopColor="#a855f7" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="edge-website-schema" x1={POS.website.x} y1={POS.website.y} x2={POS.schema.x} y2={POS.schema.y} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#5eead4" stopOpacity="0.9" />
          <stop offset="1" stopColor="#a855f7" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      <Edge from={POS.gbp}     to={POS.website} active={websitePowered}                    stroke="url(#edge-gbp-website)" />
      <Edge from={POS.gbp}     to={POS.schema}  active={schemaPowered}                     stroke="url(#edge-gbp-schema)" />
      <Edge from={POS.website} to={POS.schema}  active={websitePowered && schemaPowered}   stroke="url(#edge-website-schema)" />

      {/* Citations feed both GBP + Website */}
      <Edge from={CITATION_SUB_POS.citations100}     to={POS.gbp}     active={powerUps.citations100}     stroke={CITATION_EDGE_COLORS.citations100} />
      <Edge from={CITATION_SUB_POS.citations100}     to={POS.website} active={powerUps.citations100}     stroke={CITATION_EDGE_COLORS.citations100} />
      <Edge from={CITATION_SUB_POS.vaultCitations}   to={POS.gbp}     active={powerUps.vaultCitations}   stroke={CITATION_EDGE_COLORS.vaultCitations} />
      <Edge from={CITATION_SUB_POS.vaultCitations}   to={POS.website} active={powerUps.vaultCitations}   stroke={CITATION_EDGE_COLORS.vaultCitations} />
      <Edge from={CITATION_SUB_POS.premiumCitations} to={POS.gbp}     active={powerUps.premiumCitations} stroke={CITATION_EDGE_COLORS.premiumCitations} />
      <Edge from={CITATION_SUB_POS.premiumCitations} to={POS.website} active={powerUps.premiumCitations} stroke={CITATION_EDGE_COLORS.premiumCitations} />

      {/* Press release also feeds GBP + Website */}
      <Edge from={PRESS_RELEASE_POS} to={POS.gbp}     active={powerUps.pressRelease} stroke={PRESS_RELEASE_EDGE_COLOR} />
      <Edge from={PRESS_RELEASE_POS} to={POS.website} active={powerUps.pressRelease} stroke={PRESS_RELEASE_EDGE_COLOR} />

      <Edge from={POS.gbp} to={GBP_SUB_POS.products} active={powerUps.gbpProducts} stroke="#fb923c" />
      <Edge from={POS.gbp} to={GBP_SUB_POS.services} active={powerUps.gbpServices} stroke="#fb923c" />
      <Edge from={POS.gbp} to={GBP_SUB_POS.pictures} active={powerUps.gbpPictures} stroke="#fb923c" />
      <Edge from={POS.gbp} to={GBP_SUB_POS.reviews}  active={powerUps.gbpReviews}  stroke="#fb923c" />

      <Edge from={POS.website} to={WEBSITE_SUB_POS.landingPage}   active={powerUps.locationLandingPage} stroke="#5eead4" />
      <Edge from={POS.website} to={WEBSITE_SUB_POS.servicePages}  active={powerUps.servicePages}        stroke="#5eead4" />
      <Edge from={POS.website} to={WEBSITE_SUB_POS.locationPages} active={powerUps.locationPages}       stroke="#5eead4" />

      {/* Per-entity schema edges: Basic = LocalBusiness; Super = the other 7 */}
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.webPage}       active={schemaSubs.webPage}       stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.webSite}       active={schemaSubs.webSite}       stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.organization}  active={schemaSubs.organization}  stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.localBusiness} active={schemaSubs.localBusiness} stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.services}      active={schemaSubs.services}      stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.faq}           active={schemaSubs.faq}           stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.imageObject}   active={schemaSubs.imageObject}   stroke="#a855f7" />
      <Edge from={POS.schema} to={SCHEMA_SUB_POS.person}        active={schemaSubs.person}        stroke="#a855f7" />

      {showGraphLinks &&
        GRAPH_LINK_PAIRS.filter(([a, b]) => schemaSubs[a] && schemaSubs[b]).map(
          ([a, b], i) => (
            <motion.line
              key={`graph-${a}-${b}`}
              x1={SCHEMA_SUB_POS[a].x}
              y1={SCHEMA_SUB_POS[a].y}
              x2={SCHEMA_SUB_POS[b].x}
              y2={SCHEMA_SUB_POS[b].y}
              stroke="#c084fc"
              strokeWidth={1.2}
              strokeOpacity={0.55}
              strokeDasharray="3 4"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.7 }}
              transition={{ duration: 0.8, delay: i * 0.05, ease: "easeOut" }}
            />
          ),
        )}

      {websitePowered && <DataPulse from={POS.gbp} to={POS.website} duration={1.4} />}
      {schemaPowered && <DataPulse from={POS.gbp} to={POS.schema} duration={1.55} />}
      {websitePowered && schemaPowered && <DataPulse from={POS.website} to={POS.schema} duration={1.3} />}

      <AnimatePresence>
        {allCitations && <StarOrbit key="star-orbit" />}
      </AnimatePresence>
    </svg>
  );
}

function Edge({
  from,
  to,
  active,
  stroke,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  active: boolean;
  stroke: string;
}) {
  return (
    <motion.line
      x1={from.x}
      y1={from.y}
      x2={to.x}
      y2={to.y}
      stroke={active ? stroke : "rgba(255,255,255,0.08)"}
      strokeWidth={active ? 2 : 1}
      strokeDasharray={active ? undefined : "4 6"}
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    />
  );
}

function DataPulse({
  from,
  to,
  color = "#ffffff",
  duration = 1.4,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  color?: string;
  duration?: number;
}) {
  return (
    <motion.circle
      r={4}
      fill={color}
      style={{ filter: `drop-shadow(0 0 6px ${color})` }}
      initial={{ cx: from.x, cy: from.y }}
      animate={{ cx: to.x, cy: to.y }}
      transition={{
        duration,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
      }}
    />
  );
}

function StarOrbit() {
  const t = useMotionValue(0);

  useEffect(() => {
    const controls = animate(t, 2 * Math.PI, {
      duration: 10,
      repeat: Infinity,
      ease: "linear",
    });
    return () => controls.stop();
  }, [t]);

  const x = useTransform(t, (val) => TRINITY_CENTROID.x + 275 * Math.sin(2 * val));
  const y = useTransform(t, (val) => TRINITY_CENTROID.y + 340 * Math.sin(val));

  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      aria-hidden
    >
      <motion.g style={{ x, y }}>
        <path
          d="M 0,-14 L 4.1,-4.3 L 14,-4.3 L 6.5,1.6 L 9,11 L 0,5.5 L -9,11 L -6.5,1.6 L -14,-4.3 L -4.1,-4.3 Z"
          fill="#fcd34d"
          stroke="#fef3c7"
          strokeWidth="0.8"
          style={{
            filter:
              "drop-shadow(0 0 6px #fcd34d) drop-shadow(0 0 14px rgba(252,211,77,0.6))",
          }}
        />
      </motion.g>
    </motion.g>
  );
}

// -----------------------------------------------------------------------------
// Nodes
// -----------------------------------------------------------------------------

function TrinityNode({
  center,
  label,
  sublabel,
  accent,
  icon: Icon,
  powered,
  delay,
  shielded,
}: {
  center: { x: number; y: number };
  label: string;
  sublabel: string;
  accent: AccentKey;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  powered: boolean;
  delay: number;
  shielded?: boolean;
}) {
  const a = accentStyles[accent];
  return (
    <motion.div
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.55, delay: delay + 0.3, ease: "easeOut" }}
      className="absolute flex flex-col items-center gap-2"
      style={{
        left: center.x - NODE / 2,
        top: center.y - NODE / 2,
        width: NODE,
      }}
    >
      <motion.div
        animate={
          powered
            ? {
                boxShadow: [
                  "0 0 20px rgba(255,255,255,0.12)",
                  "0 0 50px rgba(255,255,255,0.28)",
                  "0 0 20px rgba(255,255,255,0.12)",
                ],
              }
            : undefined
        }
        transition={
          powered
            ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
            : undefined
        }
        className={cn(
          "relative flex h-28 w-28 items-center justify-center rounded-full border-2 transition-all",
          powered
            ? cn("border-white/25 bg-white/[0.04]", a.glow)
            : "border-white/10 bg-white/[0.02]",
        )}
      >
        {shielded && <ShieldRing />}
        <Icon
          className={cn("h-10 w-10", powered ? a.text : "text-white/25")}
          strokeWidth={1.5}
        />
        {!powered && (
          <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-black">
            <Lock className="h-3 w-3 text-white/40" />
          </div>
        )}
      </motion.div>
      <div className="mt-1 text-center">
        <div
          className={cn(
            "text-xs font-semibold uppercase tracking-[0.22em]",
            powered ? a.text : "text-white/40",
          )}
        >
          {label}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/30">
          {sublabel}
        </div>
      </div>
    </motion.div>
  );
}

function SubNode({
  center,
  label,
  icon: Icon,
  powered,
  delay,
  accent = "teal",
}: {
  center: { x: number; y: number };
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  powered: boolean;
  delay: number;
  accent?: AccentKey;
}) {
  const a = accentStyles[accent];
  return (
    <motion.div
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, delay: delay + 0.3, ease: "easeOut" }}
      className="absolute flex flex-col items-center gap-1.5"
      style={{
        left: center.x - SUB_NODE / 2,
        top: center.y - SUB_NODE / 2,
        width: SUB_NODE,
      }}
    >
      <motion.div
        animate={
          powered
            ? {
                boxShadow: [
                  `0 0 12px rgba(${a.shadowBase}, 0.18)`,
                  `0 0 32px rgba(${a.shadowBase}, 0.45)`,
                  `0 0 12px rgba(${a.shadowBase}, 0.18)`,
                ],
              }
            : undefined
        }
        transition={
          powered
            ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
            : undefined
        }
        className={cn(
          "relative flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 transition-all",
          powered
            ? cn("border-white/25 bg-white/[0.04]", a.glow)
            : "border-white/10 bg-white/[0.02]",
        )}
      >
        <Icon
          className={cn("h-6 w-6", powered ? a.text : "text-white/25")}
          strokeWidth={1.5}
        />
        {!powered && (
          <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-black">
            <Lock className="h-2.5 w-2.5 text-white/40" />
          </div>
        )}
      </motion.div>
      <div
        className={cn(
          "text-center text-[10px] font-semibold uppercase tracking-[0.2em]",
          powered ? a.text : "text-white/35",
        )}
      >
        {label}
      </div>
    </motion.div>
  );
}

function ShieldRing() {
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: [0.6, 1, 0.6], scale: [0.94, 1.06, 0.94] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      className="pointer-events-none absolute -inset-8"
    >
      <Shield
        className="h-full w-full text-emerald-400 drop-shadow-[0_0_30px_rgba(52,211,153,0.6)]"
        strokeWidth={1.5}
        fill="rgba(52, 211, 153, 0.10)"
      />
    </motion.div>
  );
}

export default LevelFifteenView;
